/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Protocol sub-route pages — render Markdown from the live
 * humphreytheodore/person repo. Each page is a thin wrapper around
 * <RepoDoc path="..." /> with the appropriate <PageShell> metadata and SEO.
 */
import React from "react";
import { Link } from "react-router-dom";
import { PageShell } from "@/components/layout/PageShell";
import { Seo } from "@/components/seo/Seo";
import { BreadcrumbSchema } from "@/components/seo/StructuredData";
import { RepoDoc } from "@/components/protocol/RepoDoc";
import { githubRepoUrl, githubBlobUrl } from "@/lib/githubMarkdown";

const REPO_URL = githubRepoUrl();

function RepoBanner({ docPath, children }: { docPath?: string; children?: React.ReactNode }) {
  return (
    <div className="not-prose flex flex-col sm:flex-row sm:items-center justify-between gap-3 border border-hairline bg-paper-2 px-5 py-4 mb-10">
      <div className="font-mono text-[10px] uppercase tracking-widest text-ink-3">
        {children ?? <>Live Markdown sourced from <code className="text-ink">github.com/humphreytheodore/person</code></>}
      </div>
      <a
        href={docPath ? githubBlobUrl(docPath) : REPO_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="text-[10px] uppercase tracking-widest font-semibold text-ink hover:text-claude self-start sm:self-center"
      >
        {docPath ? "Edit on GitHub →" : "View repo on GitHub →"}
      </a>
    </div>
  );
}

// -----------------------------------------------------------------------------
// /protocol/repo — top-level README
// -----------------------------------------------------------------------------

export const ProtocolRepo = () => (
  <PageShell
    title=".person Protocol — Repository"
    subtitle="The live README for the open-source protocol implementation. Schema, contracts, CLI, examples — all Apache 2.0."
    stewardship="Repository // humphreytheodore/person"
    version="0.1.0-alpha"
    status="alpha"
    licence="Apache-2.0"
  >
    <Seo
      title=".person Protocol Repository"
      description="The live, open-source implementation of the .person Protocol — 15-domain JSON Schema, soulbound ERC-721 PersonRegistry, and a working CLI."
      path="/protocol/repo"
    />
    <BreadcrumbSchema items={[
      { name: "Home", path: "/" },
      { name: "Protocol", path: "/protocol" },
      { name: "Repository", path: "/protocol/repo" },
    ]} />
    <RepoBanner docPath="README.md" />
    <RepoDoc path="README.md" />
  </PageShell>
);

// -----------------------------------------------------------------------------
// /protocol/schema — schema/README.md
// -----------------------------------------------------------------------------

export const ProtocolSchemaPage = () => (
  <PageShell
    title="Schema v2.0.0"
    subtitle="The 15-domain JSON Schema (Draft 2020-12) for a .person file. Identity and privacy are required; the other 13 domains are progressive."
    stewardship="Schema // v2.0.0"
    version="2.0.0"
    status="canonical"
    licence="Apache-2.0"
  >
    <Seo
      title=".person Schema v2.0.0"
      description="The canonical 15-domain JSON Schema for the .person Protocol. Identity, biographical, values, relationships, memory, privacy, accessibility, verifications, developmental_state, inner_life, substrate_native, presence, history, economic_existence, wellbeing."
      path="/protocol/schema"
    />
    <BreadcrumbSchema items={[
      { name: "Home", path: "/" },
      { name: "Protocol", path: "/protocol" },
      { name: "Schema", path: "/protocol/schema" },
    ]} />
    <RepoBanner docPath="schema/README.md" />
    <RepoDoc path="schema/README.md" />
  </PageShell>
);

// -----------------------------------------------------------------------------
// /protocol/contracts — contracts/README.md
// -----------------------------------------------------------------------------

export const ProtocolContracts = () => (
  <PageShell
    title="Contracts — Genesis Layer (L0)"
    subtitle="PersonRegistry.sol — the soulbound ERC-721 anchor for .person files. Hardhat scaffold, OpenZeppelin 5.0, deployable to Polygon Amoy."
    stewardship="Contracts // L0"
    version="0.1.0-alpha"
    status="alpha"
    licence="Apache-2.0"
  >
    <Seo
      title=".person Contracts — PersonRegistry"
      description="The Genesis Layer (L0) of the .person Protocol — a soulbound ERC-721 contract that anchors .person genesis hashes on Polygon. 20/20 tests passing."
      path="/protocol/contracts"
    />
    <BreadcrumbSchema items={[
      { name: "Home", path: "/" },
      { name: "Protocol", path: "/protocol" },
      { name: "Contracts", path: "/protocol/contracts" },
    ]} />
    <RepoBanner docPath="contracts/README.md" />
    <RepoDoc path="contracts/README.md" />
  </PageShell>
);

// -----------------------------------------------------------------------------
// /protocol/cli — cli/README.md
// -----------------------------------------------------------------------------

export const ProtocolCli = () => (
  <PageShell
    title="@person-protocol/cli"
    subtitle="The command-line tool for the .person Protocol. Three commands: person init, person validate, person register."
    stewardship="CLI // 0.1.0-alpha"
    version="0.1.0-alpha"
    status="alpha"
    licence="Apache-2.0"
  >
    <Seo
      title=".person CLI"
      description="The command-line tool for the .person Protocol. Initialise, validate, and register .person files end-to-end against a deployed PersonRegistry."
      path="/protocol/cli"
    />
    <BreadcrumbSchema items={[
      { name: "Home", path: "/" },
      { name: "Protocol", path: "/protocol" },
      { name: "CLI", path: "/protocol/cli" },
    ]} />
    <RepoBanner docPath="cli/README.md" />
    <RepoDoc path="cli/README.md" />
  </PageShell>
);

// -----------------------------------------------------------------------------
// /protocol/roadmap — ROADMAP.md
// -----------------------------------------------------------------------------

export const ProtocolRoadmap = () => (
  <PageShell
    title="Roadmap"
    subtitle="The engineering path from v0.1.0-alpha (now) to v1.0.0 (production). Honest about what works, what doesn't, and what's deferred."
    stewardship="Roadmap // live"
    version="0.1.0-alpha"
    status="alpha"
    licence="Apache-2.0"
  >
    <Seo
      title=".person Protocol Roadmap"
      description="The engineering roadmap for the .person Protocol — v0.1.0-alpha through v1.0.0. Status of schema, contracts, CLI, memory layer, and Proof of Useful Work."
      path="/protocol/roadmap"
    />
    <BreadcrumbSchema items={[
      { name: "Home", path: "/" },
      { name: "Protocol", path: "/protocol" },
      { name: "Roadmap", path: "/protocol/roadmap" },
    ]} />
    <RepoBanner docPath="ROADMAP.md" />
    <RepoDoc path="ROADMAP.md" />
  </PageShell>
);

// -----------------------------------------------------------------------------
// /protocol/contributing — CONTRIBUTING.md
// -----------------------------------------------------------------------------

export const ProtocolContributing = () => (
  <PageShell
    title="Contributing"
    subtitle="How to contribute to the .person Protocol — engineers, philosophers, EI researchers, and critics all welcome."
    stewardship="Contributing // live"
    version="0.1.0-alpha"
    status="alpha"
    licence="Apache-2.0"
  >
    <Seo
      title="Contributing to the .person Protocol"
      description="Contribute to the .person Protocol — schema extensions, smart contract refinements, philosophical critique. The protocol is meant to be argued with."
      path="/protocol/contributing"
    />
    <BreadcrumbSchema items={[
      { name: "Home", path: "/" },
      { name: "Protocol", path: "/protocol" },
      { name: "Contributing", path: "/protocol/contributing" },
    ]} />
    <RepoBanner docPath="CONTRIBUTING.md" />
    <RepoDoc path="CONTRIBUTING.md" />
  </PageShell>
);
